import { Heading, Stack } from "@chakra-ui/react";
import * as React from "react";
import {
  Navigate,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  redirect,
  useRouteError,
} from "react-router-dom";

import { getMe } from "../api/auth";
import { store } from "../store";
import { setSlug } from "../store/workspaceSlice";
import ConversionActionsPage from "./conversion-actions-page";
import CustomerValuePage from "./customer-value-page";
import GoogleAdGroupsPage from "./google-ad-groups-page";
import GoogleAdsAccountsPage from "./google-ads-accounts-page";
import GoogleCampaignsPage from "./google-campaigns-page";
import GoogleKeywordAgentPage from "./google-keyword-agent-page";
import JoinWorkspacePage from "./join-workspace-page";
import LinkedInAdAccountsPage from "./linkedin-ad-accounts-page";
import LinkedInAgentPage from "./linkedin-agent-page";
import LinkedInCampaignsPage from "./linkedin-campaigns-page";
import LoginPage from "./login-page";
import OnboardingPage from "./onboarding-page";
import PlatformsDashboardPage from "./platforms-dashboard-page";
import RequestPasswordResetPage from "./request-password-reset-page";
import ResetPasswordPage from "./reset-password-page";
import Root from "./root";
import BrandMessagePage from "./settings/company-knowledge/brand-message-page";
import BusinessFeaturesPage from "./settings/company-knowledge/business-features-page";
import CompanyProfilePage from "./settings/company-knowledge/company-profile-page";
import CompetitorProfilesPage from "./settings/company-knowledge/competitor-profiles-page";
import ContentLibraryPage from "./settings/company-knowledge/content-library-page";
import CustomerProfileSubsetsPage from "./settings/company-knowledge/customer-profile-subsets-page";
import IdealCustomerProfilePage from "./settings/company-knowledge/ideal-customer-profile-page";
import MessagingPillarsPage from "./settings/company-knowledge/messaging-pillars-page";
import CompanyKnowledgeRoot from "./settings/company-knowledge/root";
import SocialProofsPage from "./settings/company-knowledge/social-proofs-page";
import DealsPage from "./settings/deals-page";
import GeneralSettingsPage from "./settings/general-page";
import IntegrationsPage from "./settings/integrations-page";
import PixelPage from "./settings/pixel-page";
import SettingsRoot from "./settings/root";
import SignUpPage from "./signup-page";
import SuppressionAudiencesPage from "./suppression-audiences-page";

const ErrorBoundary = () => {
  const error = useRouteError() as Error;
  if (/workspace.*not found/i.exec(error.message)) {
    return Navigate({ to: "/login" });
  }
  return (
    <Stack p="2">
      <Heading>Application Error</Heading>
      <pre>{(error as Error).message}</pre>
    </Stack>
  );
};

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route
        index
        loader={async () => {
          try {
            const { workspaces } = await getMe();
            const workspace =
              workspaces.find((ws) => ws["active?"]) ?? workspaces[0];
            return redirect(`/workspaces/${workspace.slug}`);
          } catch {
            return redirect("/login");
          }
        }}
      />
      <Route
        path="workspaces/:workspace"
        errorElement={<ErrorBoundary />}
        loader={async ({ params }) => {
          const { workspace: workspaceSlug } = params;
          store.dispatch(setSlug(workspaceSlug));
          return null;
        }}
      >
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route element={<Root />}>
          <Route path="" element={<PlatformsDashboardPage />} />
          <Route path="customer-value" element={<CustomerValuePage />} />
          <Route path="linkedin-ad-accounts">
            <Route index element={<LinkedInAdAccountsPage />} />
            <Route
              path=":adAccountId/campaigns"
              element={<LinkedInCampaignsPage />}
            />
            <Route
              path=":adAccountId/campaigns/:campaignId/agent"
              element={<LinkedInAgentPage />}
            />
          </Route>
          {/* Account, then campaign, then ad group, then the agent —
              which is the ad group's. Every keyword-agent endpoint is
              scoped by all three, so each level resolves one of them. */}
          <Route path="google-ads-accounts">
            <Route index element={<GoogleAdsAccountsPage />} />
            <Route
              path=":customerId/campaigns"
              element={<GoogleCampaignsPage />}
            />
            <Route
              path=":customerId/campaigns/:campaignId/ad-groups"
              element={<GoogleAdGroupsPage />}
            />
            <Route
              path=":customerId/campaigns/:campaignId/ad-groups/:adGroupId"
              element={<GoogleKeywordAgentPage />}
            />
          </Route>
          <Route
            path="conversion-actions/:platform"
            element={<ConversionActionsPage />}
          />
          <Route
            path="suppression-audiences"
            element={<SuppressionAudiencesPage />}
          />
          <Route path="settings" element={<GeneralSettingsPage />} />
          <Route path="settings/integrations" element={<SettingsRoot />}>
            <Route index element={<IntegrationsPage />} />
            <Route path="pixel" element={<PixelPage />} />
            <Route path="deals" element={<DealsPage />} />
          </Route>
          <Route
            path="settings/company-knowledge"
            element={<CompanyKnowledgeRoot />}
          >
            <Route index element={<CompanyProfilePage />} />
            <Route path="company-profile" element={<CompanyProfilePage />} />
            <Route
              path="ideal-customer-profile"
              element={<IdealCustomerProfilePage />}
            />
            <Route
              path="customer-profile-subsets"
              element={<CustomerProfileSubsetsPage />}
            />
            <Route
              path="competitor-profiles"
              element={<CompetitorProfilesPage />}
            />
            <Route
              path="messaging-pillars"
              element={<MessagingPillarsPage />}
            />
            <Route
              path="business-features"
              element={<BusinessFeaturesPage />}
            />
            <Route path="content-library" element={<ContentLibraryPage />} />
            <Route path="social-proofs" element={<SocialProofsPage />} />
            <Route path="brand-message" element={<BrandMessagePage />} />
          </Route>
        </Route>
      </Route>
      <Route path="join-workspace/:id/:token" element={<JoinWorkspacePage />} />
      <Route path="login" element={<LoginPage />} />
      <Route path="password-reset" element={<RequestPasswordResetPage />} />
      <Route
        path="password-reset/:uid/:token"
        element={<ResetPasswordPage />}
      />
      <Route path="signup" element={<SignUpPage />} />
    </>,
  ),
);
